import React, { useEffect, useRef, useMemo } from 'react';
import { Department, StaffMember } from '../../lib/types';

export type ReportType = 'all' | 'staff' | 'vendor' | 'department' | 'monthly' | 'status' | 'category';

interface Props {
  reportType: ReportType; setReportType: (t: ReportType) => void;
  staffFilter: string; setStaffFilter: (s: string) => void;
  deptFilter: string; setDeptFilter: (s: string) => void;
  dateFrom: string; setDateFrom: (s: string) => void;
  dateTo: string; setDateTo: (s: string) => void;
  activeStaff: StaffMember[];
  activeDepts: Department[];
  exporting: boolean;
  onExportCSV: () => void;
  onPrint: () => void;
}

const REPORT_OPTIONS: { v: ReportType; l: string; short: string }[] = [
  { v: 'all', l: 'All Orders', short: 'All Orders' },
  { v: 'staff', l: 'By Staff Member', short: 'By Staff' },
  { v: 'vendor', l: 'By Vendor', short: 'By Vendor' },
  { v: 'department', l: 'By Department', short: 'By Department' },
  { v: 'monthly', l: 'Monthly Summary', short: 'Monthly Summary' },
  { v: 'status', l: 'By Status', short: 'By Status' },
  { v: 'category', l: 'By Category', short: 'By Category' },
];

const dateInputStyle: React.CSSProperties = {
  fontSize: 12, padding: '6px 8px', borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg)',
  color: 'var(--text)', fontFamily: 'inherit',
};

const exportBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', fontSize: 12, fontWeight: 500,
  color: 'var(--text)', background: 'var(--surface2)',
  border: '1px solid var(--border)', borderRadius: 8,
  cursor: 'pointer', fontFamily: 'inherit',
};

function useOutsideClick<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, onClose]);
  return ref;
}

interface DropButtonProps {
  label: string;
  active: boolean;
  weight?: number;
  onClick: () => void;
}
function DropButton({ label, active, weight, onClick }: DropButtonProps) {
  return (
    <button onClick={onClick} className="filter-sel"
      style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: weight }}>
      {label}
      <svg viewBox="0 0 12 12" style={{ width: 10, height: 10, fill: 'var(--text3)' }}><path d="M2 4l4 4 4-4"/></svg>
    </button>
  );
}

function MenuOption({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`menu-item ${selected ? 'is-active' : ''}`}
    >
      {selected
        ? <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        : <span style={{ width: 12 }} />}
      <span className="flex-1 text-truncate">{children}</span>
    </button>
  );
}

export function ReportsFilterBar(p: Props) {
  const [showReportDrop, setShowReportDrop] = React.useState(false);
  const [showStaffDrop, setShowStaffDrop] = React.useState(false);
  const [showDeptDrop, setShowDeptDrop] = React.useState(false);
  const [staffSearch, setStaffSearch] = React.useState('');

  const reportDropRef = useOutsideClick<HTMLDivElement>(showReportDrop, () => setShowReportDrop(false));
  const staffDropRef  = useOutsideClick<HTMLDivElement>(showStaffDrop,  () => { setShowStaffDrop(false); setStaffSearch(''); });
  const deptDropRef   = useOutsideClick<HTMLDivElement>(showDeptDrop,   () => setShowDeptDrop(false));

  const filteredStaffList = useMemo(() => {
    const q = staffSearch.toLowerCase().trim();
    const list = p.activeStaff.filter(s => !q || s.name.toLowerCase().includes(q));
    return list.slice(0, 5);
  }, [p.activeStaff, staffSearch]);

  const currentReport = REPORT_OPTIONS.find(o => o.v === p.reportType)?.short ?? 'All Orders';
  const currentDept = p.deptFilter === 'all' ? 'All departments' : p.activeDepts.find(d => d.id === p.deptFilter)?.name || 'All departments';

  return (
    <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', padding: '8px 12px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {/* Report type */}
      <div style={{ position: 'relative' }} ref={reportDropRef}>
        <DropButton
          label={currentReport} active={true} weight={600}
          onClick={() => { setShowReportDrop(!showReportDrop); setShowStaffDrop(false); setShowDeptDrop(false); }}
        />
        {showReportDrop && (
          <div className="menu" style={{ position: 'absolute', left: 0, top: '100%', marginTop: 4, minWidth: 180, zIndex: 9999 }}>
            {REPORT_OPTIONS.map(opt => (
              <MenuOption
                key={opt.v}
                selected={p.reportType === opt.v}
                onClick={() => { p.setReportType(opt.v); setShowReportDrop(false); }}
              >{opt.l}</MenuOption>
            ))}
          </div>
        )}
      </div>

      {/* Staff filter */}
      {(p.reportType === 'all' || p.reportType === 'staff') && (
        <div style={{ position: 'relative' }} ref={staffDropRef}>
          <DropButton
            label={p.staffFilter === 'all' ? 'All staff' : p.staffFilter}
            active={p.staffFilter !== 'all'}
            onClick={() => { setShowStaffDrop(!showStaffDrop); setShowReportDrop(false); setShowDeptDrop(false); }}
          />
          {showStaffDrop && (
            <div className="menu" style={{ position: 'absolute', left: 0, top: '100%', marginTop: 4, minWidth: 200, zIndex: 9999 }}>
              <div style={{ padding: '4px 8px 6px' }}>
                <input
                  className="input"
                  value={staffSearch} onChange={e => setStaffSearch(e.target.value)}
                  placeholder="Search staff..." autoFocus
                  style={{ padding: '6px 8px', fontSize: 12 }}
                />
              </div>
              <MenuOption selected={p.staffFilter === 'all'} onClick={() => { p.setStaffFilter('all'); setShowStaffDrop(false); setStaffSearch(''); }}>All staff</MenuOption>
              {filteredStaffList.map(s => (
                <MenuOption key={s.id} selected={p.staffFilter === s.name} onClick={() => { p.setStaffFilter(s.name); setShowStaffDrop(false); setStaffSearch(''); }}>
                  {s.name}
                </MenuOption>
              ))}
              {staffSearch && filteredStaffList.length === 0 && (
                <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text3)' }}>No matches</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Department filter */}
      {(p.reportType === 'all' || p.reportType === 'department') && (
        <div style={{ position: 'relative' }} ref={deptDropRef}>
          <DropButton
            label={currentDept}
            active={p.deptFilter !== 'all'}
            onClick={() => { setShowDeptDrop(!showDeptDrop); setShowReportDrop(false); setShowStaffDrop(false); }}
          />
          {showDeptDrop && (
            <div className="menu" style={{ position: 'absolute', left: 0, top: '100%', marginTop: 4, minWidth: 180, zIndex: 9999 }}>
              <MenuOption selected={p.deptFilter === 'all'} onClick={() => { p.setDeptFilter('all'); setShowDeptDrop(false); }}>All departments</MenuOption>
              {p.activeDepts.map(d => (
                <MenuOption key={d.id} selected={p.deptFilter === d.id} onClick={() => { p.setDeptFilter(d.id); setShowDeptDrop(false); }}>
                  {d.name}
                </MenuOption>
              ))}
            </div>
          )}
        </div>
      )}

      <input type="date" value={p.dateFrom} onChange={e => p.setDateFrom(e.target.value)} title="From date" style={dateInputStyle} />
      <span style={{ fontSize: 10, color: 'var(--text3)' }}>to</span>
      <input type="date" value={p.dateTo} onChange={e => p.setDateTo(e.target.value)} title="To date" style={dateInputStyle} />
      {(p.dateFrom || p.dateTo) && (
        <button onClick={() => { p.setDateFrom(''); p.setDateTo(''); }}
          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--text2)', cursor: 'pointer', padding: '4px 10px', fontFamily: 'inherit' }}>Clear</button>
      )}

      <div style={{ flex: 1 }} />

      <button onClick={p.onExportCSV} disabled={p.exporting} title="Export every matching order as CSV"
        style={{ ...exportBtnStyle, cursor: p.exporting ? 'default' : 'pointer', opacity: p.exporting ? 0.6 : 1 }}>
        <svg viewBox="0 0 16 16" style={{ width: 13, height: 13, fill: 'none', stroke: 'var(--text2)', strokeWidth: 1.3 }}><path d="M8 2v8M5 7l3 3 3-3"/><path d="M2 12h12"/></svg>
        {p.exporting ? 'Exporting…' : 'CSV'}
      </button>
      <button onClick={p.onPrint} title="Print or save as PDF" style={exportBtnStyle}>
        <svg viewBox="0 0 16 16" style={{ width: 13, height: 13, fill: 'none', stroke: 'var(--text2)', strokeWidth: 1.3 }}><rect x="3" y="6" width="10" height="7" rx="1.5"/><path d="M5 6V3h6v3"/><circle cx="11" cy="8.5" r="0.7" fill="var(--text2)"/></svg>
        PDF
      </button>
    </div>
  );
}
